import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CalendarCheck, Menu, Phone, X } from 'lucide-react';
import { LanguageToggle } from '@/components/ui/language-toggle';
import { OceanneLogo } from '@/components/ui/oceanne-logo';
import { useCalendly } from '@/lib/CalendlyContext';
import { useLeadModal } from '@/lib/LeadModalContext';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { useActiveSection } from '@/lib/hooks/use-active-section';
import { getLenis } from '@/lib/hooks/use-lenis';
import { t } from '@/lib/translations';
import { cn } from '@/lib/utils';

const PHONE = '+14383826496';

const NAV_LINKS = [
  { key: 'apropos', hash: 'about' },
  { key: 'methode', hash: 'methode' },
  { key: 'offre', hash: 'offre' },
  { key: 'ressources', to: '/ressources' },
  { key: 'faq', hash: 'faq' },
  { key: 'contact', hash: 'contact' }
] as const;

/**
 * Phase 27.48 — scroll-to-top helper utilisé par le logo + le lien Accueil.
 * Si on est déjà sur `/`, TanStack Router ne change pas la location.pathname
 * et le useEffect de __root.tsx ne fire pas → scroll manuel explicite ici.
 * Lenis si actif (desktop), window.scrollTo sinon (mobile / a11y).
 */
function scrollToTop() {
  const lenis = getLenis();
  if (lenis) lenis.scrollTo(0, { duration: 1.1 });
  else window.scrollTo({ top: 0, behavior: 'smooth' });
}

const SECTION_IDS = NAV_LINKS.flatMap((l) => ('hash' in l ? [l.hash] : []));

export function Navbar() {
  const { lang } = useLang();
  const { open: openCalendly } = useCalendly();
  const { openForm } = useLeadModal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const activeSection = useActiveSection(SECTION_IDS);

  const navText = ta(t.nav, lang);
  const callAria = typeof t.mobile.call_aria === 'string' ? t.mobile.call_aria : t.mobile.call_aria[lang];
  const calendlyAria = typeof t.mobile.calendly_aria === 'string' ? t.mobile.calendly_aria : t.mobile.calendly_aria[lang];

  // Phase 27.27/28 — scroll-aware morph : sticky outer (preserves Hero offset),
  // inner container morphs into floating pill on scroll.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleCalendlyClick = () => openCalendly();

  return (
    <nav
      className={cn(
        'sticky top-0 z-50 w-full transition-[padding,background-color] duration-500 ease-out',
        scrolled ? 'bg-transparent pt-3 md:pt-4 pb-3' : 'bg-background border-b border-border/40'
      )}
    >
      {/* Skip-link — WCAG 2.4.1 keyboard nav bypass */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:font-sans focus:text-sm focus:font-bold focus:text-primary-foreground"
      >
        {ta(t.a11y, lang).skip_to_content}
      </a>

      {/* Inner container — morphs into floating pill on scroll.
          Phase 27.28 — pattern Buteau adapté à sticky : pill shrink + rounded-full
          + backdrop-blur + bg/85 + hairline terracotta border + shadow warm. */}
      <div
        className={cn(
          'mx-auto flex items-center justify-between gap-x-4 md:gap-x-8 transition-all duration-500 ease-out',
          scrolled
            ? 'max-w-[1280px] mx-3 md:mx-auto px-4 md:px-6 py-2 md:py-2.5 rounded-full border border-border/60 bg-background/85 backdrop-blur-xl shadow-[0_8px_32px_-12px_oklch(0_0_0/0.15)]'
            : 'max-w-[1440px] px-5 md:px-20 py-3 md:py-4 border border-transparent'
        )}
        style={
          scrolled
            ? {
                // Hairline terracotta inner ring + soft outer halo for floating pill depth
                boxShadow:
                  '0 8px 32px -12px oklch(0 0 0 / 0.15), inset 0 1px 0 color-mix(in oklch, var(--primary) 12%, transparent)'
              }
            : undefined
        }
      >
        {/* Logo — Link to home + onClick scroll-to-top (Phase 27.48).
            Phase 27.52 — vrai logo iA officiel (iA + éléphant) devant le nom.
            object-position/object-fit pour cropper et n'afficher que la
            portion "iA + éléphant" (le texte "Groupe financier" du logo
            full est masqué via aspect-ratio + object-fit: cover). */}
        <Link
          to="/"
          onClick={scrollToTop}
          aria-label="Océanne Brodeur — Distributrice agréée iA Groupe Financier"
          className={cn(
            'inline-flex items-center gap-2.5 md:gap-3 font-display text-foreground tracking-tight transition-all duration-500 ease-out',
            scrolled ? 'text-[clamp(1.25rem,2vw,1.625rem)]' : 'text-[clamp(1.5rem,2.5vw,1.875rem)]'
          )}
        >
          {/* Logo iA compact — crop CSS du full pour ne montrer que iA+éléphant
              (le texte "Groupe financier" est en bas, masqué via aspect-ratio +
              object-fit: cover + object-position: top). <picture> négocie
              AVIF → WebP → PNG selon support browser. */}
          <picture>
            <source srcSet="/partners/ia-groupe-financier.avif" type="image/avif" />
            <source srcSet="/partners/ia-groupe-financier.webp" type="image/webp" />
            <img
              src="/partners/ia-groupe-financier-480w.png"
              alt=""
              aria-hidden="true"
              width={480}
              height={220}
              className={cn(
                'w-auto object-cover object-top transition-all duration-500 ease-out shrink-0',
                scrolled ? 'h-[1.6em]' : 'h-[1.8em]'
              )}
              style={{ aspectRatio: '480 / 220' }}
            />
          </picture>
          {/* Separator thin vertical sage 50% — espace visuel sans peser */}
          <span
            className="inline-block h-[0.8em] w-px"
            style={{ backgroundColor: 'color-mix(in oklch, var(--secondary) 50%, transparent)' }}
            aria-hidden="true"
          />
          {/* Phase 27.83 — leaf sprig logo Océanne (sage) validé par cliente.
              Themable via currentColor : text-secondary par défaut. */}
          <OceanneLogo className="h-[1.4em] w-auto shrink-0 text-secondary" />
          <span className="whitespace-nowrap">{t.brand}</span>
        </Link>

        {/* Desktop nav — Accueil en 1er (Phase 27.48) + sections hash.
            TanStack Link to="/" hash="X" works cross-route : from /lexique
            or /carnet, clicking #methode SPA-navigates home + scrolls.
            Active link highlighted via useActiveSection (scroll-spy on home).
            Accueil actif quand activeSection est null (top de page). */}
        <ul className="hidden md:flex items-center gap-5 lg:gap-6">
          <li>
            <Link
              to="/"
              onClick={scrollToTop}
              className={cn(
                // Phase 27.79 a11y : text-primary text-sm cream = ratio 3.53 < 4.5
                // WCAG AA. Switch text-foreground (contrast ratio ~9.5) sur tous
                // les états, ajout underline accent for active state.
                'inline-block font-sans text-sm uppercase tracking-wider transition-all duration-300 ease-out hover:scale-110 hover:tracking-widest text-foreground hover:text-primary',
                activeSection ? '' : 'underline underline-offset-4 decoration-primary'
              )}
            >
              {navText.accueil}
            </Link>
          </li>
          {NAV_LINKS.map((link) => {
            const isActive = 'hash' in link && activeSection === link.hash;
            const cls = cn(
              'inline-block whitespace-nowrap font-sans text-sm uppercase tracking-wider transition-all duration-300 ease-out hover:scale-110 hover:tracking-widest',
              isActive ? 'text-primary' : 'text-foreground hover:text-primary'
            );
            return (
              <li key={link.key}>
                {'to' in link ? (
                  <Link to={link.to} className={cls}>
                    {navText[link.key]}
                  </Link>
                ) : (
                  <Link to="/" hash={link.hash} className={cls}>
                    {navText[link.key]}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        {/* Desktop right cluster — Phone + Calendly icons + Lang toggle + CTA */}
        <div className="hidden md:flex items-center gap-3">
          {/* Phase 27.27 — Phone icon outline terracotta (pattern cross-Intralys). */}
          <a
            href={`tel:${PHONE}`}
            aria-label={callAria}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-primary/30 text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/55 hover:bg-primary/5 active:scale-95"
          >
            <Phone className="w-4 h-4" strokeWidth={1.7} />
          </a>

          {/* Phase 27.27 — Calendly icon outline. Ouvre CalendlyModal via context. */}
          <button
            type="button"
            onClick={handleCalendlyClick}
            aria-label={calendlyAria}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-primary/30 text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/55 hover:bg-primary/5 active:scale-95 cursor-pointer"
          >
            <CalendarCheck className="w-4 h-4" strokeWidth={1.7} />
          </button>

          <LanguageToggle />
          {/* Phase 27.28 — CTA attention pulse terracotta (signal subtle, opt-out via prefers-reduced-motion). */}
          <button
            type="button"
            onClick={openForm}
            data-cursor="cta"
            // Phase 27.79 a11y : white-on-terracotta + darker bg pour WCAG AA pass.
            className="cta-pulse-terracotta inline-flex items-center justify-center rounded-full bg-[oklch(0.51_0.135_38)] px-6 py-2 font-sans text-xs font-bold uppercase tracking-widest text-white hover:brightness-110 transition-all"
          >
            {navText.consultation}
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-muted transition-colors"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu drawer — Phase 27.28 cascade reveal via data-state + stagger CSS.
          Conditionally rendered (vs always-mounted) to keep a11y clean :
          axe-core (JSDOM) doesn't honor `inert` so always-mounted closed drawer
          flags aria-hidden-focus violations. Closing animation traded for a11y. */}
      {mobileMenuOpen && (
      <div
        data-state="open"
        className="md:hidden overflow-hidden border-t border-border/40 bg-background"
      >
        <ul className="oceanne-mobile-menu flex flex-col gap-1 px-5 py-4">
          {/* Accueil en 1er (Phase 27.48) — onClick scrollToTop + close menu */}
          <li style={{ '--stagger-delay': '80ms' } as React.CSSProperties}>
            <Link
              to="/"
              onClick={() => {
                setMobileMenuOpen(false);
                scrollToTop();
              }}
              className="block py-3 font-sans text-sm uppercase tracking-wider text-foreground"
            >
              {navText.accueil}
            </Link>
          </li>
          {NAV_LINKS.map((link, i) => (
            <li key={link.key} style={{ '--stagger-delay': `${(i + 1) * 60 + 80}ms` } as React.CSSProperties}>
              {'to' in link ? (
                <Link
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 font-sans text-sm uppercase tracking-wider text-foreground"
                >
                  {navText[link.key]}
                </Link>
              ) : (
                <Link
                  to="/"
                  hash={link.hash}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 font-sans text-sm uppercase tracking-wider text-foreground"
                >
                  {navText[link.key]}
                </Link>
              )}
            </li>
          ))}
          <li className="pt-2" style={{ '--stagger-delay': `${(NAV_LINKS.length + 1) * 60 + 80}ms` } as React.CSSProperties}>
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                openForm();
              }}
              className="inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 font-sans text-xs font-bold uppercase tracking-widest text-primary-foreground"
            >
              {navText.consultation}
            </button>
          </li>
          <li
            className="pt-2 flex justify-center"
            style={{ '--stagger-delay': `${(NAV_LINKS.length + 2) * 60 + 80}ms` } as React.CSSProperties}
          >
            <LanguageToggle />
          </li>
        </ul>
      </div>
      )}
    </nav>
  );
}
